
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useCookies, Cookies } from 'react-cookie';
import { useNavigate } from 'react-router';
import tick from './images/tick.svg';
import tickAlt from './images/tick-alt.svg';
import cross from './images/cross.svg';
import crossAlt from './images/cross-alt.svg';
import checklist from './images/checklist.svg';
import warningRed from './images/warning-red.svg';
import warningOrange from './images/warning-orange.svg';
import certificate from './images/certificate.svg';
import calendar from './images/calendar.svg';
import calendarRed from './images/calendar-red.svg';
import mileageGray from './images/mileage-gray.svg';
import mileageGreen from './images/mileage-green.svg';
import mileageRed from './images/mileage-red.svg';
import backArrow from './images/back-arrow.svg';
import history from './images/history.svg';
import Home from './home.tsx';
import coveredVehicle from "./images/covered-vehicle.webp";
import increase from "./images/increase.svg";

export function checkMOTValid(date: Date){
  const today = new Date();
  const expiryDate = new Date(date);
  return (expiryDate.getTime() > today.getTime())
}

export function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("en-GB");
}

export function formatDateToMonthYear(dateString: string) {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ];

  return `${months[new Date(dateString).getMonth()]} ${new Date(dateString).getFullYear()}`;
}

function CheckMOT() {
  const navigate = useNavigate();
  const [cookies, setCookie] = useCookies<string>([""]);
  const { registration } = useParams();

  const [imageUrl, setImageUrl] = useState("");
  const [reg, setReg] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [color, setColor] = useState("");
  const [manufactureDate, setManufactureDate] = useState("");
  const [fuelType, setFuelType] = useState("");
  const [engineSize, setEngineSize] = useState("");
  const [recall, setRecall] = useState("");
  const [currentMOT, setCurrentMOT] = useState("");
  const [currentMileage, setCurrentMileage] = useState("");
  const [previousMOTs, setPreviousMOTs] = useState<motTests[]>([]);
  const [noOfMOTs, setNoOfMOTs] = useState(0);
  const [noOfPassedMOT, setNoOfPassedMOT] = useState(0);
  const [noOfFailedMOT, setNoOfFailedMOT] = useState(0);

  interface ResponseData{
    MOTData: MOTData;
    imageData: imageData;
  }

  interface imageData{
    url: string;
  }

  interface MOTData {
    registration: string;
    make: string;
    model: string;
    firstUsedDate: string;
    fuelType: string;
    primaryColour: string;
    registrationDate: string;
    manufactureDate: string;
    engineSize: string;
    hasOutstandingRecall: string;
    motTests: motTests[];

    data:string;
  }

  interface motTests{
    registrationAtTimeOfTest: string;
    motTestNumber: string;
    completedDate: string;
    expiryDate: Date;
    odometerValue: string;
    odometerUnit: string;
    odometerResultType: string;
    testResult: string;
    dataSource: string;
    defects: defects[];
    mileageDifference: number;
  }

  interface defects{
    dangerous: boolean;
    text: string;
    type: string;
  }

  async function fetchVehicle(){
    try{
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/reg/${registration}`);

      if (response.status == 404){
        console.log(`Reg: '${registration}' not recognised`)
        navigate("/", {state : {message : `Reg '${registration}' not recognised`}})
        return;
      }
      const {MOTData, imageData} : ResponseData = await response.json();
     
      setImageUrl(imageData.url);
      setReg(MOTData.registration);
      setMake(MOTData.make);
      setModel(MOTData.model);
      setColor(MOTData.primaryColour);
      setFuelType(MOTData.fuelType);
      setEngineSize(MOTData.engineSize || "N/A");
      setManufactureDate(formatDate(MOTData.manufactureDate));

      if(MOTData.motTests){
        setCurrentMileage(MOTData.motTests[0].odometerValue);
        if(checkMOTValid(MOTData.motTests[0].expiryDate)){
          setCurrentMOT(`Valid until ${formatDate(new Date(MOTData.motTests[0].expiryDate))}`);
        }
        else{
          setCurrentMOT(`MOT expired`);
        }

        setPreviousMOTs(MOTData.motTests);

        setNoOfMOTs(MOTData.motTests.length);
        setNoOfPassedMOT(MOTData.motTests.filter(MOT => MOT.testResult === "PASSED").length);
        setNoOfFailedMOT(MOTData.motTests.filter(MOT => MOT.testResult === "FAILED").length);

        for(let i = 0; i < MOTData.motTests.length - 1; i++){
          MOTData.motTests[i].mileageDifference = MOTData.motTests[i].odometerValue - MOTData.motTests[i+1].odometerValue;
        }
        setPreviousMOTs(MOTData.motTests);
      }


    }
    catch(error){
      console.log("Internal Server Error");
      navigate("/", {state : {message : 'Something went wrong'}});
    }
  }

  function displayMOTstatus(){
    if(currentMOT === "MOT expired"){
      return `MOT EXPIRED ${formatDate((previousMOTs[0].expiryDate) || new Date(new Date(manufactureDate).setFullYear(new Date(manufactureDate).getFullYear() + 3)))}`;
    }
    else{
      return "MOT PASS";
    }
  }
  
  useEffect(() => {
    fetchVehicle();
  }, [registration]);

  useEffect(() => {
    if (reg) {
      setCookie(reg, {make, model, manufactureDate: manufactureDate.slice(-4), imageUrl, mileage: currentMileage, createdAt: new Date()}, {
        path: "/",
        maxAge: 60 * 60 * 24 * 30,
      });
    }
  }, [reg]);

  return (
    <div className='mot-check'>
      <div className='back-btn'>
        <img src={backArrow} />
        <a href='/' className='home-btn'> Back to home</a>
      </div>
      <div className='vehicle-details-container'>
        <img src={imageUrl || coveredVehicle} 
          alt="vehicle image"
          style={{width: '400px'}}>
        </img>
        <div>
          <p style={{fontSize:'10px', fontWeight: "500"}}>VEHICLE DETAILS</p>
          <h2>{make} {model}</h2>
          <div className='vehicle-detail'>
            <p>Make:</p>
            <p>{make}</p>
          </div>
          <div className='vehicle-detail'>
            <p>Model:</p>
            <p>{model}</p>
          </div>
          <div className='vehicle-detail'>
            <p>Colour:</p>
            <p>{color}</p>
          </div>
          <div className='vehicle-detail'>
            <p>Fuel: </p>
            <p>{fuelType}</p>
          </div>
          <div className='vehicle-detail'>
            <p>Engine Size: </p>
            <p>{engineSize}</p>
          </div>
          <div className='vehicle-detail'>
            <p>Manufacture Date:</p>
            <p>{manufactureDate}</p>
          </div>
        </div>
      </div>
      {previousMOTs.length == 0? "" : (
      <>
        <div className={currentMOT !== "MOT expired" ? 'mot-status-container-passed' : 'mot-status-container-expired'}>
          <img 
            src={currentMOT !== "MOT expired" 
            ? tick : cross} 
            style={{height: "80px"}}
            alt="MOT valid image"
          />
          <div>
            <h2>{displayMOTstatus()}</h2>
            <div>
              <img 
                src={currentMOT !== "MOT expired" ? calendar : calendarRed}
                alt="MOT certificate image"
              />
              <p>{currentMOT}</p>
            </div>
            <div>
              <img 
                src={currentMOT !== "MOT expired" ? mileageGreen : mileageRed}
                alt="odometer image"
                style={{width:"20px"}}
              />
              <p>Last recorded mileage: {Number(currentMileage).toLocaleString()} mi</p>
            </div>
          </div>
        </div>
        <div className='passes-and-fails-container'>
          <div className='passes-card'>
            <img src={tickAlt}/>
            <div>
              <p>{noOfPassedMOT}</p>
              <p>MOT PASSES</p>
            </div>
          </div>
          <div className='passes-card'>
            <img src={crossAlt}/>
            <div>
              <p>{noOfFailedMOT}</p>
              <p>MOT FAILS</p>
            </div>
          </div>
          <div className='fails-card'>
            <img src={certificate}/>
            <div>
              <p>{noOfMOTs}</p>
              <p>TOTAL MOTS</p>
            </div>
          </div>
        </div>
        <div className='mot-history-container'>
          <div style={{display: "flex", columnGap: "6px", alignItems: "center"}}>
            <img src={history} style={{height: "34px"}}/>
            <h2> MOT History </h2>
          </div>
          {previousMOTs.map((MOT, index) => (
            <div key={MOT.motTestNumber} className={`${MOT.testResult === "PASSED" ? 'mot-history-card-pass' : 'mot-history-card-fail'}`}>
              <div className='mot-history-header'>
                <h3>{formatDateToMonthYear(MOT.completedDate)}</h3>
                <div className={MOT.testResult === "PASSED"? "mot-passed" : "mot-failed"}>
                  <img src={MOT.testResult === "PASSED" ? tick : cross}/>
                  <p>{MOT.testResult}</p>
                </div>
              </div>
            <div className='mot-history-defects-container'>
              <div className='mot-history-defects-left'>
                <div className='mot-history-mileage-container'>
                  <img src={mileageGray} style={{height: "20px"}}/>
                  <p className='mot-history-mileage'><span style={{color: "#71849c"}}>Mileage: </span>{MOT.odometerValue? Number(MOT.odometerValue).toLocaleString() : ""} mi</p>
                  {MOT.mileageDifference > 0 ? 
                  <div className='mot-history-mileage-difference-positive'>
                    <img src={increase}/>
                    <p>+{MOT.mileageDifference.toLocaleString()}</p>
                  </div>
                    :
                    MOT.mileageDifference < 0 ? 
                    <p className='mot-history-mileage-difference-negative'>-{MOT.mileageDifference.toLocaleString()}</p>
                    :
                    <p className='mot-history-mileage-difference-unchanged'>-</p>
                  }
                </div>
                <div className='mot-history-defect-count-container'>
                {MOT.defects.filter(defect => defect.type === "DANGEROUS").length != 0 ? (
                  <div className='mot-history-defect-count'>
                    <div className='defect-type DANGEROUS'>
                      <img src={warningRed} style={{height: "16px"}}/>
                      <p>{MOT.defects.filter(defect => defect.type === "DANGEROUS").length} Dangerous</p>
                    </div>
                  </div>
                  ) : null }
                {MOT.defects.filter(defect => defect.type === "MAJOR" || defect.type === "PRS" ||  defect.type === "FAIL").length != 0 ? (
                  <div className='mot-history-defect-count'>
                    <div className='defect-type MAJOR'>
                      <img src={warningRed} style={{height: "16px"}}/>
                      <p>{MOT.defects.filter(defect => defect.type === "MAJOR" || defect.type === "PRS" ||  defect.type === "FAIL").length} Major</p>
                    </div>
                  </div>
                  ) : null }
                {MOT.defects.filter(defect => defect.type === "ADVISORY" ||  defect.type === "MINOR" ||  defect.type === "USER ENTERED").length != 0 ? (
                <div className='mot-history-defect-count'>
                  <div className='defect-type ADVISORY'>
                    <img src={warningOrange} style={{height: "16px"}}/>
                    <p>{MOT.defects.filter(defect => defect.type === "ADVISORY" || defect.type === "MINOR" ||  defect.type === "USER ENTERED").length} Advisory</p>
                  </div>
                </div>                
                ) : null }  
                </div>
              </div>
              <div>
                {MOT.defects.map((defect) => (
                <div key={defect.text} className="mot-defect">
                  <div className={`defect-type ${defect.type}`}>
                    <img src={defect.type === "MAJOR" || defect.type === "DANGEROUS" || defect.type === "PRS" || defect.type === "FAIL" ? warningRed : warningOrange} style={{height: "16px"}} />
                    <p>{defect.type.charAt(0).toUpperCase() + defect.type.slice(1).toLowerCase()}</p>
                  </div>
                  <p>{defect.text}</p>
                </div>
                ))}
              </div>
            </div>
            </div>
          ))}
        </div>
      </>
    )}
  </div>
  );
}

export default CheckMOT;
